import type { ComponentSharedSeo } from '@autobid/strapi-integration/graphql/graphql'
import type { BarometerCar, DetailsCarItemData } from '@autobid/ui/types/Car'
import type { PreparedBarometerAuction } from '@autobid/ui/types/components/AuctionsData'
import type { Ref } from 'vue'
import { formatPrice } from '@autobid/ui/utils/formatPrice'

function getMetaLabeledValue([label, value]: [
  string,
  string | number | undefined,
]) {
  return value ? `${label}: ${value}` : null
}

export function useCarSeoMeta(
  strapiSeo: ComponentSharedSeo | null,
  car: Ref<BarometerCar | DetailsCarItemData | undefined>,
  auction: Ref<PreparedBarometerAuction | undefined | null>,
) {
  if (!car.value)
    return // car can be unpublished and removed from application state via WS

  const { t } = useI18n()

  const setMetaProperty = (
    elements: Array<string | number | undefined | null>,
  ) => {
    const result = elements.filter(Boolean).join(', ')
    return result.length ? result : undefined
  }

  const getSocialTags = (prefix: 'og' | 'twitter') => {
    return {
      [`${prefix}Title`]: () => setMetaProperty([car.value?.name]),
      [`${prefix}Description`]: () => setMetaProperty([
        strapiSeo?.metaDescription,
        getMetaLabeledValue([
          t('auction-list.auction-number'),
          auction.value?.number,
        ]),
        getMetaLabeledValue([
          t('barometer.catalog-number'),
          car.value?.catalogNumber,
        ]),
        auction.value?.startDate,
        car.value?.name,
        car.value?.price.minimal ? formatPrice(car.value?.price.minimal) : undefined,
      ]),
      [`${prefix}Image`]: () => setMetaProperty([
        Object.values(car.value?.imageGroups.itemMainImageSubgroup ?? {})?.[0]
          ?.links
          ?.hd,
      ]),
      [`${prefix}ImageAlt`]: () => setMetaProperty([car.value?.name]),
      // the size is not known because api doesn't return this type of info
      [`${prefix}ImageHeight`]: () => undefined,
      [`${prefix}ImageWidth`]: () => undefined,
    }
  }

  useSeoMeta({
    title: () => setMetaProperty([car.value?.name]) || '',
    description: () =>
      setMetaProperty([
        strapiSeo?.metaDescription,
        auction.value?.title,
        getMetaLabeledValue([
          t('auction-list.auction-number'),
          auction.value?.number,
        ]),
        getMetaLabeledValue([
          t('barometer.catalog-number'),
          car.value?.catalogNumber,
        ]),
        auction.value?.startDate,
        car.value?.name,
        car.value?.price.minimal ? formatPrice(car.value?.price.minimal) : undefined,
      ]),
    keywords: () =>
      setMetaProperty([
        strapiSeo?.keywords,
        car.value?.name,
        car.value?.category?.name,
        car.value?.equipments.eq70?.name,
      ]),
    ...getSocialTags('og'),
    ...getSocialTags('twitter'),
  })
}
